// usePermissions.js
// TrapperKeeper permission system
// Resolves user permissions from Azure AD group membership + local overrides
//
// Permission levels (highest to lowest):
//   admin   - full access including admin tab, project config changes
//   modify  - can modify existing data, cannot change project config
//   write   - can write/capture data, cannot modify or delete
//   view    - read-only access
//   none    - no access (LockedTab)
//
// Resolution order:
//   1. userOverrides in project config (highest priority - instant fix)
//   2. AD group membership mapped to permission level
//   3. Default level for authenticated users (fallback)
//   4. Public flag on tab (bypasses all checks)

import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

// Permission level hierarchy - higher index = more access
const LEVELS = ["none", "view", "write", "modify", "admin"];

export function levelIndex(level) {
  return LEVELS.indexOf(level || "none");
}

export function hasPermission(userLevel, requiredLevel) {
  return levelIndex(userLevel) >= levelIndex(requiredLevel);
}

// Fetch user's Azure AD group memberships
async function fetchUserGroups(accessToken) {
  try {
    const res = await fetch(GRAPH_BASE + "/me/memberOf?$select=id,displayName", {
      headers: { Authorization: "Bearer " + accessToken },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.value || []).map(function(g) { return g.displayName; });
  } catch (e) {
    console.warn("[permissions] Could not fetch groups:", e.message);
    return [];
  }
}

// Resolve permission level for a user given project permission config
//
// Three-tier group hierarchy (highest to lowest priority within groups):
//   Tier 1: TrapperKeeper-[Client]-[Project]-[Level]  - client project specific
//   Tier 2: TrapperKeeper-TCC-[Project]-[Level]        - TCC project specific
//   Tier 3: TrapperKeeper-TCC-[Level]                  - TCC global
//
// Full resolution order:
//   1. userOverrides (instant fix, highest priority)
//   2. Client project group  e.g. TrapperKeeper-ACME-DataPlatform-View
//   3. TCC project group     e.g. TrapperKeeper-TCC-DataPlatform-Write
//   4. TCC global group      e.g. TrapperKeeper-TCC-Admin
//   5. defaultLevel          (fallback)

export function resolvePermissionLevel(userEmail, userGroups, permissionConfig) {
  if (!permissionConfig) return "write";

  const {
    groups = {},
    userOverrides = {},
    defaultLevel = "write",
    projectId = "",
    clientId = "",
  } = permissionConfig;

  // 1. User override - highest priority, instant fix
  const emailLower = (userEmail || "").toLowerCase();
  for (const [email, level] of Object.entries(userOverrides)) {
    if (email.toLowerCase() === emailLower) return level;
  }

  // Build group name patterns for this project
  // e.g. for projectId="data-platform" clientId="acme":
  //   client project: TrapperKeeper-ACME-DataPlatform-[Level]
  //   TCC project:    TrapperKeeper-TCC-DataPlatform-[Level]
  //   TCC global:     TrapperKeeper-TCC-[Level]
  const projectSuffix = projectId
    ? projectId.split("-").map(function(w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join("")
    : "";
  const clientPrefix = clientId ? clientId.toUpperCase() : "";

  const tierPatterns = [
    // Tier 1: client project specific
    clientPrefix && projectSuffix ? "TrapperKeeper-" + clientPrefix + "-" + projectSuffix + "-" : null,
    // Tier 2: TCC project specific
    projectSuffix ? "TrapperKeeper-TCC-" + projectSuffix + "-" : null,
    // Tier 3: TCC global
    "TrapperKeeper-TCC-",
  ].filter(Boolean);

  // Check explicit group config first (from project JSON)
  let highestFromConfig = "none";
  for (const groupName of (userGroups || [])) {
    const configLevel = groups[groupName];
    if (configLevel && levelIndex(configLevel) > levelIndex(highestFromConfig)) {
      highestFromConfig = configLevel;
    }
  }
  if (highestFromConfig !== "none") return highestFromConfig;

  // Then check by naming convention tier by tier
  for (const pattern of tierPatterns) {
    let highestInTier = "none";
    for (const groupName of (userGroups || [])) {
      if (groupName.startsWith(pattern)) {
        // Extract level from end of group name
        const suffix = groupName.slice(pattern.length).toLowerCase();
        if (LEVELS.includes(suffix) && levelIndex(suffix) > levelIndex(highestInTier)) {
          highestInTier = suffix;
        }
      }
    }
    if (highestInTier !== "none") return highestInTier;
  }

  // 5. Default level
  return defaultLevel;
}

// Resolve tab-specific permission requirement
export function resolveTabPermission(tabName, permissionConfig) {
  if (!permissionConfig) return { minLevel: "view", public: false };

  const { tabOverrides = {} } = permissionConfig;
  const override = tabOverrides[tabName];

  if (override) return override;

  // Default tab permissions by category
  const defaults = {
    "Status":                   { minLevel: "view",   public: true },
    "Admin":                    { minLevel: "admin",  public: false },
    "New Hire Onboarding Tracker": { minLevel: "view", public: false },
  };

  // Capture tabs require write
  if (tabName.includes("Capture") || tabName.includes("Planner")) {
    return { minLevel: "write", public: false };
  }

  return defaults[tabName] || { minLevel: "view", public: false };
}

// ─── MAIN HOOK ────────────────────────────────────────────────────────────────

export default function usePermissions(projectPermissionConfig, guestEmail) {
  const { instance, accounts } = useMsal();

  const [userLevel, setUserLevel] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userEmail = accounts[0]?.username || "";
  const isAuthenticated = accounts.length > 0;

  const loadPermissions = useCallback(async function() {
    if (!isAuthenticated) {
      setUserLevel("none");
      setLoading(false);
      return;
    }

    try {
      // Get token with GroupMember.Read scope if available
      // Falls back gracefully if scope not granted
      let groups = [];
      try {
        const tokenResp = await instance.acquireTokenSilent({
          ...loginRequest,
          scopes: [...(loginRequest.scopes || []), "GroupMember.Read.All"],
          account: accounts[0],
        });
        groups = await fetchUserGroups(tokenResp.accessToken);
      } catch (e) {
        // GroupMember.Read.All not granted - use user overrides only
        console.warn("[permissions] Group fetch failed, using user overrides only:", e.message);
      }

      setUserGroups(groups);
      const level = resolvePermissionLevel(userEmail, groups, projectPermissionConfig);
      setUserLevel(level);
    } catch (e) {
      console.error("[permissions] Error:", e.message);
      setError(e.message);
      setUserLevel("view"); // Safe fallback
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userEmail, instance, accounts, projectPermissionConfig]);

  useEffect(function() {
    loadPermissions();
  }, [loadPermissions]);

  // Check if user can access a specific tab
  function canAccessTab(tabName) {
    const tabPerm = resolveTabPermission(tabName, projectPermissionConfig);

    // Public tabs always accessible
    if (tabPerm.public) return true;

    // Not authenticated
    if (!isAuthenticated) return false;

    // Check level
    return hasPermission(userLevel, tabPerm.minLevel);
  }

  // Check specific permission level
  function can(action) {
    const actionLevels = {
      view:   "view",
      write:  "write",
      modify: "modify",
      admin:  "admin",
    };
    return hasPermission(userLevel, actionLevels[action] || "view");
  }

  // Guest bypass: guestEmail provided means bypass user - return view-only permissions
  if (guestEmail) {
    var GUEST_CAPTURE_TABS = ['Readiness Capture','Use Case Capture','Data Platform Capture',
      'Pilot Planning Capture','Governance Capture','Story Capture','Execution Capture',
      'Current State Capture','Day 1 Wrap Capture','Summit Close Capture','Acceleration Capture','Admin'];
    return {
      userLevel: 'view',
      userGroups: [],
      userEmail: guestEmail,
      isAuthenticated: true,
      loading: false,
      error: null,
      canAccessTab: function(tabName) { return !GUEST_CAPTURE_TABS.includes(tabName); },
      can: function(action) { return action === 'view'; },
      reload: function() {}
    };
  }

  // ── PE / external viewer bypass ──────────────────────────────────────────
  if (guestEmail) {
    var VIEWER_BLOCKED = [
      "Readiness Capture","Use Case Capture","Data Platform Capture",
      "Pilot Planning Capture","Governance Capture","Story Capture",
      "Execution Capture","Current State Capture","Day 1 Wrap Capture",
      "Summit Close Capture","Admin","New Hire Onboarding Tracker"
    ];
    return {
      userLevel: "view",
      userGroups: [],
      userEmail: guestEmail,
      isAuthenticated: true,
      loading: false,
      error: null,
      canAccessTab: function(tabName) { return !VIEWER_BLOCKED.includes(tabName); },
      can: function(action) { return action === "view"; },
      reload: function() {}
    };
  }
  // ─────────────────────────────────────────────────────────────────────────

  return {
    userLevel,
    userGroups,
    userEmail,
    isAuthenticated,
    loading,
    error,
    canAccessTab,
    can,
    reload: loadPermissions,
  };
}
